import type { ReactNode } from 'react'
import { InfoTip } from '../../ui/InfoTip'
import './note.css'

/**
 * A note, read where it is called.
 *
 * On paper a note is set at the foot or gathered at the back, and which of
 * those is a real trade-off: close and cramped, or roomy and far. **Neither
 * applies on a screen.** A screen can show the note beside the sentence that
 * called it and take it away again, so it does — and the placement setting
 * governs print only.
 *
 * That is also why this is not a link to the bottom of the page. Sending a
 * reader to the back and expecting them to find their way home is a paper
 * compromise; reproducing it here would be copying the limitation rather than
 * the intent.
 *
 * **Where the pane is open, the marker reveals the note THERE instead.** One
 * rule, so a marker never offers two ways to read the same note: the panel is
 * for glancing at one, the pane for keeping them all beside a long read, and
 * whichever is open is the one a click answers in.
 *
 * The panel behavior is `InfoTip`'s — anchored to the marker, portalled so
 * nothing clips it, and a Modal only where the prose genuinely will not fit,
 * measured at open. Shared rather than rebuilt: a second popover is how an
 * interface ends up with several that dismiss differently.
 */
export function Note({
  marker,
  noteId,
  children,
  onReveal,
}: {
  /** What the marker prints. A string: it is not always a number. */
  marker: string
  /** Addressable, so the list beside the text can bring the reader to the call
   *  that made this note — the same journey as a marker, in the other
   *  direction. */
  noteId: string
  children: ReactNode
  /** Supplied while the pane is open — then the marker reveals it there. */
  onReveal?: () => void
}) {
  if (onReveal) {
    return (
      <button
        type="button"
        className="qnote__call"
        aria-label={`Note ${marker}`}
        data-noteref={noteId}
        onClick={onReveal}
      >
        <sup className="qnote__marker" data-anchor-skip>
          {marker}
        </sup>
      </button>
    )
  }

  return (
    <InfoTip
      size="bare"
      label={`Note ${marker}`}
      trigger={
        <sup className="qnote__marker" data-anchor-skip data-noteref={noteId}>
          {marker}
        </sup>
      }
    >
      <div className="qnote__body">
        {/* The number is repeated inside, so the panel says which note it is
            once it is no longer beside the marker that opened it. */}
        <span className="qnote__number" aria-hidden>
          {marker}
        </span>
        <div>{children}</div>
      </div>
    </InfoTip>
  )
}
